import { compact } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import {
  ComplianceProgramAppliesTo,
  ComplianceProgramRecurrence,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import RecipientForm from "@apps/artifact/components/recipient-form";
import TeamPicker from "@apps/reporting/components/team-picker";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar, isAdminVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import DeprecatedSelect from "@components/select/select";
import { assertNonNull } from "@helpers/helpers";

import ComplianceProgramFormProgramPeriod from "./compliance-program-form-program-period";
import ComplianceProgramFormStartDueDates from "./compliance-program-form-start-due-dates";
import {
  ComplianceProgramType,
  DateRangeType,
  ProgramForm,
  ProgramStartTrigger,
} from "./types";

const ComplianceProgramFormTopFields = ({
  complianceProgramType,
  creator,
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  canUpdate,
  excludedUsers,
  onUpdateExcludedUsers,
  isSaving,
  appliesToTeams,
  onUpdateAppliesToTeams,
  appliesToManagers,
  onUpdateAppliesToManagers,
  appliesToUsers,
  onUpdateAppliesToUsers,
  selectedProgramPeriodRange,
  setSelectedProgramPeriodRange,
}: {
  complianceProgramType: ComplianceProgramType;
  creator: BasicUser;
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: ProgramForm) => void;
  canUpdate: boolean;
  excludedUsers: { id: number; name: string }[];
  onUpdateExcludedUsers: (data: { id: number; name: string }[]) => void;
  isSaving: boolean;
  appliesToTeams: { id: number; title: string }[];
  onUpdateAppliesToTeams: (data: { id: number; title: string }[]) => void;
  appliesToManagers: { id: number; name: string }[];
  onUpdateAppliesToManagers: (data: { id: number; name: string }[]) => void;
  appliesToUsers: { id: number; name: string }[];
  onUpdateAppliesToUsers: (data: { id: number; name: string }[]) => void;
  selectedProgramPeriodRange: DateRangeType;
  setSelectedProgramPeriodRange: (range: DateRangeType) => void;
}) => {
  const label = useLabel();
  const isAdmin = isAdminVar();
  const currentUser = currentUserVar();

  const appliesToOptions = useMemo(
    () =>
      compact([
        isAdmin && {
          value: ComplianceProgramAppliesTo.Organization,
          label: "All employees",
        },
        isAdmin && {
          value: ComplianceProgramAppliesTo.Managers,
          label: "All managers",
        },
        isAdmin && {
          value: ComplianceProgramAppliesTo.Ics,
          label: "All individual contributors (ICs)",
        },
        {
          value: ComplianceProgramAppliesTo.CreatorDirectReports,
          label: `${
            creator.id === currentUser.id
              ? "All my"
              : `All of ${creator.name}'s`
          } direct reports`,
        },
        {
          value: ComplianceProgramAppliesTo.CreatorManagementTree,
          label: `${
            creator.id === currentUser.id
              ? "All my"
              : `All of ${creator.name}'s`
          } reports (full reporting tree)`,
        },
        {
          value: ComplianceProgramAppliesTo.Departments,
          label: `All employees in specific ${label("team", {
            pluralize: true,
          })}`,
        },
        {
          value: ComplianceProgramAppliesTo.ReportsTo,
          label: "All employees that report to specific managers",
        },
        {
          value: ComplianceProgramAppliesTo.Users,
          label: "Specific employees",
        },
      ]),
    [creator, currentUser, isAdmin, label]
  );

  return (
    <div className="py-10 border-b">
      <FormFields
        fields={compact([
          {
            title: "Title",
            render: () => (
              <Input
                aria-label="Compliance program title input"
                value={proposedComplianceProgram.title ?? ""}
                onChange={(e) =>
                  onUpdateProposedComplianceProgram({
                    ...proposedComplianceProgram,
                    title: e.target.value,
                  })
                }
              />
            ),
          },
          {
            title: "Program start trigger",
            render: () => (
              <DeprecatedSelect
                containerClassName="flex-1"
                disabled={!canUpdate}
                value={
                  proposedComplianceProgram.ongoing
                    ? ProgramStartTrigger.EMPLOYEE_START_DATE
                    : ProgramStartTrigger.ON_START_DATE
                }
                options={[
                  {
                    value: ProgramStartTrigger.ON_START_DATE,
                    label: "Automatically on start date",
                  },
                  {
                    value: ProgramStartTrigger.EMPLOYEE_START_DATE,
                    label: "Number of days after employee start date",
                  },
                ]}
                onChange={(opt) => {
                  const isOngoing =
                    opt.value === ProgramStartTrigger.EMPLOYEE_START_DATE;
                  onUpdateProposedComplianceProgram({
                    ...proposedComplianceProgram,
                    startDate: null,
                    startDateOffset: null,
                    dueDate: null,
                    startDateDueDateOffset: null,
                    ongoing: isOngoing,
                    recurrence: isOngoing
                      ? ComplianceProgramRecurrence.None
                      : proposedComplianceProgram.recurrence,
                  });
                }}
              />
            ),
          },
          {
            title: "Start and due date",
            render: () => (
              <ComplianceProgramFormStartDueDates
                proposedComplianceProgram={proposedComplianceProgram}
                onUpdateProposedComplianceProgram={
                  onUpdateProposedComplianceProgram
                }
              />
            ),
          },
          complianceProgramType !== ComplianceProgramType.ENGAGEMENT_SURVEY && {
            title: "Program period",
            render: () => (
              <ComplianceProgramFormProgramPeriod
                proposedComplianceProgram={proposedComplianceProgram}
                onUpdateProposedComplianceProgram={
                  onUpdateProposedComplianceProgram
                }
                selectedProgramPeriodRange={selectedProgramPeriodRange}
                setSelectedProgramPeriodRange={setSelectedProgramPeriodRange}
              />
            ),
          },
          {
            title: "Applies to",
            render: () => (
              <ComboboxGeneric
                options={appliesToOptions}
                disabled={!canUpdate}
                onChangeValue={(opt) =>
                  onUpdateProposedComplianceProgram({
                    ...proposedComplianceProgram,
                    appliesTo: opt.value,
                  })
                }
                value={assertNonNull(
                  appliesToOptions.find(
                    ({ value }) => value === proposedComplianceProgram.appliesTo
                  )
                )}
              />
            ),
          },
          proposedComplianceProgram.appliesTo ===
            ComplianceProgramAppliesTo.Departments && {
            title: `${label("team", { pluralize: true, capitalize: true })}`,
            render: () => (
              <TeamPicker
                canChange={canUpdate}
                teams={appliesToTeams}
                onAddTeam={(team) => {
                  onUpdateAppliesToTeams(appliesToTeams.concat(team));
                }}
                onRemoveTeam={(team) => {
                  onUpdateAppliesToTeams(
                    appliesToTeams.filter((t) => t.id !== team.id)
                  );
                }}
              />
            ),
          },
          proposedComplianceProgram.appliesTo ===
            ComplianceProgramAppliesTo.ReportsTo && {
            title: "Managers",
            render: () => (
              <RecipientForm
                canChange={canUpdate}
                showCurrentUser
                recipients={appliesToManagers}
                onAddRecipient={(user) =>
                  onUpdateAppliesToManagers([...appliesToManagers, user])
                }
                onRemoveRecipient={(user) =>
                  onUpdateAppliesToManagers(
                    appliesToManagers.filter((u) => u.id !== user.id)
                  )
                }
              />
            ),
          },
          proposedComplianceProgram.appliesTo ===
            ComplianceProgramAppliesTo.Users && {
            title: "Employees",
            render: () => (
              <RecipientForm
                canChange={canUpdate}
                showCurrentUser
                recipients={appliesToUsers}
                onAddRecipient={(user) =>
                  onUpdateAppliesToUsers([...appliesToUsers, user])
                }
                onRemoveRecipient={(user) =>
                  onUpdateAppliesToUsers(
                    appliesToUsers.filter((u) => u.id !== user.id)
                  )
                }
              />
            ),
          },
          {
            title: "Excluded employees",
            render: () => (
              <RecipientForm
                canChange={canUpdate}
                showCurrentUser
                recipients={excludedUsers}
                onAddRecipient={(user) =>
                  onUpdateExcludedUsers([...excludedUsers, user])
                }
                onRemoveRecipient={(user) =>
                  onUpdateExcludedUsers(
                    excludedUsers.filter((u) => u.id !== user.id)
                  )
                }
              />
            ),
          },
          {
            title: "Exclude hired after",
            render: () => (
              <div className="flex items-center gap-4">
                <InputDatePicker
                  date={proposedComplianceProgram.excludedHireDateAfter}
                  onChangeDate={(date) =>
                    onUpdateProposedComplianceProgram({
                      ...proposedComplianceProgram,
                      excludedHireDateAfter: date
                        ? moment(date).format("YYYY-MM-DD")
                        : null,
                      removeExcludedHireDateAfter: undefined,
                    })
                  }
                  wrapperClassName="w-full"
                  placeholderText=""
                  isClearable={false}
                  ariaLabelledBy="Compliance program exclude hire date after"
                />
                {proposedComplianceProgram.excludedHireDateAfter && (
                  <Button
                    className="text-sm"
                    onClick={() =>
                      onUpdateProposedComplianceProgram({
                        ...proposedComplianceProgram,
                        excludedHireDateAfter: undefined,
                        removeExcludedHireDateAfter: true,
                      })
                    }
                    theme={buttonTheme.text}
                    disabled={isSaving}
                    text="Clear"
                  />
                )}
              </div>
            ),
          },
        ])}
      />
    </div>
  );
};

export default ComplianceProgramFormTopFields;
